import { CanopyColor, StructureColor } from '../configurations/colors';

/**
 * Calculate colors discount price
 *
 * @param {hasCanopy} hasCanopy If the pergola has a canopy
 * @param {StructureColor} structureColor The color of the pergola
 * @param {CanopyColor | ''} canopyColor The color of the canopy
 * @param {number} totalPrice The total price of the pergola
 * @param {number} canopyPrice The canopy price of the pergola
 * @param {number} canopyFlatFee The non-discountable flat fee inside canopyPrice
 * @param {number} structureCharcoalGrayDiscountPercentage The color percent multiplier
 * @param {number} structureWhiteSnowDiscountPercentage The color percent multiplier
 * @param {number} structureBrazilianIpeDiscountPercentage The color percent multiplier
 * @param {number} structureTuscanCedarDiscountPercentage The color percent multiplier
 * @param {number} canopyCappuccinoDiscountPercentage The color percent multiplier
 * @param {number} canopyDoveWhiteDiscountPercentage The color percent multiplier
 * @param {number} canopySterlingSilverDiscountPercentage The color percent multiplier
 * @param {number} canopyDesertSandDiscountPercentage The color percent multiplier
 * @param {number} canopyPacificBlueDiscountPercentage The color percent multiplier
 * @param {number} canopyCloudNineDiscountPercentage The color percent multiplier
 * @return {number}
 */

export const calculateColorsPriceDiscount = (
  hasCanopy: boolean,
  structureColor: StructureColor,
  canopyColor: CanopyColor | '',
  totalPrice: number,
  canopyPrice: number,
  canopyFlatFee: number,
  structureCharcoalGrayDiscountPercentage: number,
  structureWhiteSnowDiscountPercentage: number,
  structureBrazilianIpeDiscountPercentage: number,
  structureTuscanCedarDiscountPercentage: number,
  canopyCappuccinoDiscountPercentage: number,
  canopyDoveWhiteDiscountPercentage: number,
  canopySterlingSilverDiscountPercentage: number,
  canopyDesertSandDiscountPercentage: number,
  canopyPacificBlueDiscountPercentage: number,
  canopyCloudNineDiscountPercentage: number
): number => {
  const newTotalPrice = hasCanopy ? totalPrice - canopyPrice : totalPrice;
  // The flat fee is not discountable, so only the area based part of the
  // canopy price is eligible for the canopy color discount.
  const discountableCanopyPrice = Math.max(0, canopyPrice - canopyFlatFee);
  let discount: number = 0;

  // Structure color discount
  if (structureColor === StructureColor.CHARCOAL_GRAY) {
    discount = newTotalPrice * structureCharcoalGrayDiscountPercentage;
  }

  if (structureColor === StructureColor.HAMPTON_WHITE) {
    discount = newTotalPrice * structureWhiteSnowDiscountPercentage;
  }

  if (structureColor === StructureColor.BRAZILIAN_IPE) {
    discount = newTotalPrice * structureBrazilianIpeDiscountPercentage;
  }

  if (structureColor === StructureColor.TUSCAN_CEDAR) {
    discount = newTotalPrice * structureTuscanCedarDiscountPercentage;
  }

  // Canopy color discount
  if (hasCanopy) {
    if (canopyColor === CanopyColor.CAPPUCCINO) {
      discount += discountableCanopyPrice * canopyCappuccinoDiscountPercentage;
    }

    if (canopyColor === CanopyColor.DOVE_WHITE) {
      discount += discountableCanopyPrice * canopyDoveWhiteDiscountPercentage;
    }

    if (canopyColor === CanopyColor.STERLING_SILVER) {
      discount += discountableCanopyPrice * canopySterlingSilverDiscountPercentage;
    }

    if (canopyColor === CanopyColor.DESERT_SAND) {
      discount += discountableCanopyPrice * canopyDesertSandDiscountPercentage;
    }

    if (canopyColor === CanopyColor.PACIFIC_BLUE) {
      discount += discountableCanopyPrice * canopyPacificBlueDiscountPercentage;
    }

    if (canopyColor === CanopyColor.CLOUD_NINE) {
      discount += discountableCanopyPrice * canopyCloudNineDiscountPercentage;
    }
  }

  return discount;
};
